import { SUPPORT_GRANT_STATUS_LABELS, SUPPORT_GRANT_STATUSES } from "@station/contracts";
import { Alert, Button, Checkbox, DataTable, EmptyState, FormField, Label, Select } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Headset } from "lucide-react";
import { type ReactElement, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  usePlatformPermissions,
  useSupportGrants,
  useTenantOptions,
} from "../../features/platform/queries.js";
import { PlatformPage } from "../../features/platform/shared.js";
import { RequestSupportDialog } from "../../features/platform/tenant-dialogs.js";
import { useGrantActions, useGrantColumns } from "../../features/platform/tenant-tabs.js";
import { errorMessage } from "../../lib/errors.js";

export const Route = createFileRoute("/platform/destek")({ component: SupportRoute });

/**
 * Destek erişimleri (Prompt 24 web): personel kiracıdan neden + süreyle (≤ 8 sa) izin ister; kiracı yetkilisi onaylar,
 * reddeder ya da istediği an geri alır. Onaylı izinle SALT OKUNUR girilir; her istek kiracının göreceği kayda yazılır.
 */
function SupportRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = usePlatformPermissions();
  const tenants = useTenantOptions();
  const mineId = useId();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [mine, setMine] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const grants = useSupportGrants({ tenantId, status, mine });
  const grantActions = useGrantActions();
  const columns = useGrantColumns({ showTenant: true, actions: grantActions.render });
  const options = (tenants.data ?? [])
    .filter((tenant) => tenant.status !== "CLOSED")
    .map((tenant) => ({ id: tenant.id, name: tenant.name }));

  return (
    <PlatformPage
      title={t("platform.support.title")}
      description={t("platform.support.description")}
      permission="platform.tenants.read"
      actions={
        permissions.has("platform.support.access") ? (
          <Button onClick={() => setRequesting(true)} disabled={options.length === 0}>
            {t("platform.support.request")}
          </Button>
        ) : null
      }
    >
      <Alert tone="info">{t("platform.support.readOnlyNotice")}</Alert>
      <DataTable
        caption={t("platform.support.title")}
        columns={columns}
        rows={grants.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={grants.isPending}
        error={grants.error ? errorMessage(grants.error) : undefined}
        emptyState={<EmptyState icon={<Headset />} title={t("platform.support.empty")} />}
        filters={
          <div className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <FormField label={t("platform.fields.tenant")}>
              {(control) => (
                <Select
                  {...control}
                  value={tenantId ?? ""}
                  onChange={(event) => setTenantId(event.target.value || null)}
                >
                  <option value="">{t("platform.all")}</option>
                  {(tenants.data ?? []).map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("platform.fields.status")}>
              {(control) => (
                <Select
                  {...control}
                  value={status ?? ""}
                  onChange={(event) => setStatus(event.target.value || null)}
                >
                  <option value="">{t("platform.all")}</option>
                  {SUPPORT_GRANT_STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {SUPPORT_GRANT_STATUS_LABELS[value]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <div className="flex min-h-10 items-center gap-2">
              <Checkbox id={mineId} checked={mine} onChange={(event) => setMine(event.target.checked)} />
              <Label htmlFor={mineId}>{t("platform.support.onlyMine")}</Label>
            </div>
          </div>
        }
      />
      {requesting ? (
        <RequestSupportDialog open onOpenChange={setRequesting} tenantId={null} tenantOptions={options} />
      ) : null}
      {grantActions.dialogs}
    </PlatformPage>
  );
}
